/**
 * PhotoUploader component - Drag-and-drop file upload zone
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  UPLOAD_CONSTRAINTS,
  isValidImageType,
  isValidFileSize,
  formatFileSize
} from '../../types/photo';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

interface PhotoUploaderProps {
  onUploadStart: () => void;
  onUploadProgress: (progress: number) => void;
  onUploadComplete: (file: File) => Promise<void>;
  onUploadError: (error: string) => void;
  disabled?: boolean;
  uploading?: boolean;
}

const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  onUploadStart,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
  disabled = false,
  uploading = false
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  debugLog('[DEBUG PhotoUploader] Render, disabled:', disabled, 'uploading:', uploading);

  // Validate files
  const validateFiles = useCallback((files: FileList | File[]): { valid: File[]; errors: string[] } => {
    debugLog('[DEBUG PhotoUploader] Validating', files.length, 'files');
    const valid: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      if (!isValidImageType(file)) {
        errors.push(`${file.name}: Invalid file type. Allowed: ${UPLOAD_CONSTRAINTS.ALLOWED_TYPES.join(', ')}`);
      } else if (!isValidFileSize(file)) {
        errors.push(`${file.name}: File too large. Maximum: ${formatFileSize(UPLOAD_CONSTRAINTS.MAX_FILE_SIZE)}`);
      } else {
        valid.push(file);
      }
    });

    return { valid, errors };
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    debugLog('[DEBUG PhotoUploader] Files selected:', files.length);

    const { valid, errors } = validateFiles(files);

    if (errors.length > 0) {
      onUploadError(errors.join('\n'));
    }

    if (valid.length > 0) {
      setSelectedFiles(valid);

      // Generate previews
      const newPreviews: string[] = [];
      valid.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          newPreviews.push(e.target?.result as string);
          if (newPreviews.length === valid.length) {
            setPreviews([...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  }, [validateFiles, onUploadError]);

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) {
      setIsDragOver(true);
    }
  }, [disabled, uploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || uploading) return;

    const { files } = e.dataTransfer;
    handleFileSelect(files);
  }, [disabled, uploading, handleFileSelect]);

  // Handle click to select files
  const handleClick = useCallback(() => {
    if (!disabled && !uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled, uploading]);

  // Handle file input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelect]);

  // Handle upload button click
  const handleUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    debugLog('[DEBUG PhotoUploader] Starting upload of', selectedFiles.length, 'files');
    onUploadStart();

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const progress = Math.round(((i + 1) / selectedFiles.length) * 100);

      try {
        debugLog('[DEBUG PhotoUploader] Uploading file:', file.name);
        await onUploadComplete(file);
        onUploadProgress(progress);
      } catch (error) {
        console.error('[DEBUG PhotoUploader] Upload error:', error);
        onUploadError(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Clear selection after upload
    setSelectedFiles([]);
    setPreviews([]);
  }, [selectedFiles, onUploadStart, onUploadComplete, onUploadProgress, onUploadError]);

  // Clear selection
  const handleClear = useCallback(() => {
    setSelectedFiles([]);
    setPreviews([]);
  }, []);

  // Remove a single file from selection
  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <div className="photo-uploader">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={UPLOAD_CONSTRAINTS.ALLOWED_TYPES.join(',')}
        multiple
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {/* Drop zone */}
      <div
        className={`upload-dropzone ${isDragOver ? 'drag-over' : ''} ${disabled || uploading ? 'disabled' : ''}`}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {uploading ? (
          <div className="upload-loading">
            <div className="upload-spinner"></div>
            <p>Uploading photos...</p>
          </div>
        ) : (
          <>
            <div className="upload-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="upload-text">
              <strong>Click to upload</strong> or drag and drop
            </p>
            <p className="upload-hint">
              JPEG, PNG, or WebP (max {formatFileSize(UPLOAD_CONSTRAINTS.MAX_FILE_SIZE)})
            </p>
          </>
        )}
      </div>

      {/* File previews */}
      {selectedFiles.length > 0 && (
        <div className="upload-preview-section">
          <div className="upload-preview-header">
            <h4>Selected Files ({selectedFiles.length})</h4>
            <button
              className="upload-clear-btn"
              onClick={handleClear}
              disabled={uploading}
            >
              Clear All
            </button>
          </div>
          <div className="upload-preview-grid">
            {selectedFiles.map((file, index) => (
              <div key={index} className="upload-preview-item">
                {previews[index] && (
                  <img
                    src={previews[index]}
                    alt={file.name}
                    className="upload-preview-image"
                  />
                )}
                <div className="upload-preview-info">
                  <span className="upload-preview-name" title={file.name}>
                    {file.name}
                  </span>
                  <span className="upload-preview-size">
                    {formatFileSize(file.size)}
                  </span>
                </div>
                <button
                  className="upload-preview-remove"
                  onClick={() => handleRemoveFile(index)}
                  disabled={uploading}
                  title="Remove"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
          <button
            className="upload-submit-btn"
            onClick={handleUpload}
            disabled={uploading || selectedFiles.length === 0}
          >
            {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo${selectedFiles.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;
